import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * Onboarding footer — Back/Next/Skip buttons + page indicator dots.
 * Pinned at the bottom of the screen by the parent flex layout.
 */
export default function OnboardingFooter({
  page,
  TOTAL_PAGES,
  enabledPages,
  loading,
  hasPrimaryPick,
  isLightPagebg,
  cardStyle,
  canProceed,
  nextPage,
  prevPage,
  navigate,
  isLastSetupPage,
  user,
  completeOnboarding,
  setPage,
  enabledPagesLength,
}) {
  return (
    <div className="pb-6 pt-2 flex-shrink-0">
      {/* Final-page back button — always visible on the last page (plan
          selection / embedded payment) so users are never trapped. */}
      {page === TOTAL_PAGES && page > 1 && (
        <div className="flex justify-start mb-4">
          <Button
            variant="outline"
            onClick={prevPage}
            className={`h-10 px-4 rounded-xl dark-back-btn onb-back-btn ${hasPrimaryPick && !isLightPagebg ? 'onb-back-btn-tinted' : ''}`}
            data-testid="back-btn-final"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Back
          </Button>
        </div>
      )}

      {/* Navigation Buttons */}
      {page < TOTAL_PAGES && (
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={page > 1 ? prevPage : () => navigate('/')}
            className={`flex-shrink-0 h-12 px-4 rounded-xl dark-back-btn onb-back-btn ${hasPrimaryPick && !isLightPagebg ? 'onb-back-btn-tinted' : ''}`}
            data-testid={page > 1 ? 'back-btn' : 'back-to-landing-btn'}
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <Button
            onClick={(e) => {
              try {
                if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
                  navigator.vibrate(0);
                  navigator.vibrate(10);
                }
              } catch (_) { /* ignore */ }
              e.currentTarget.blur();
              nextPage();
            }}
            onTouchEnd={(e) => e.currentTarget.blur()}
            disabled={!canProceed() || loading}
            className={`flex-1 h-12 text-base rounded-xl gap-2 ${hasPrimaryPick ? '' : 'onb-next-btn'}`}
            style={hasPrimaryPick ? { backgroundColor: cardStyle.backgroundColor, color: '#fff' } : undefined}
            data-testid="next-btn"
          >
            {loading ? 'Saving...' : (enabledPages[page - 1]?.next_button_text || 'Next')}
            {!loading && <ArrowRight className="w-4 h-4" />}
          </Button>
          {/* Skip button for optional pages */}
          {page <= enabledPagesLength && !enabledPages[page - 1]?.required && (
            <Button
              variant="ghost"
              onClick={() => isLastSetupPage ? (user ? completeOnboarding() : setPage(TOTAL_PAGES)) : setPage(p => p + 1)}
              className={`flex-shrink-0 h-12 px-4 rounded-xl ${hasPrimaryPick && !isLightPagebg ? 'text-white/80 hover:text-white hover:bg-white/10' : 'text-gray-500'}`}
              disabled={loading}
              data-testid="skip-btn"
            >
              Skip
            </Button>
          )}
        </div>
      )}

      {/* Page indicator dots */}
      {page < TOTAL_PAGES && (
        <div className="flex justify-center gap-1.5 mt-4">
          {enabledPages.map((_, i) => {
            const isActive = i + 1 === page;
            const isPast = i + 1 < page;
            if (hasPrimaryPick) {
              const dotColor = isLightPagebg ? '#1F2937' : '#FFFFFF';
              return (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${isActive ? 'w-6' : 'w-1.5'}`}
                  style={{ backgroundColor: dotColor, opacity: isActive ? 1 : isPast ? 0.55 : 0.25 }}
                />
              );
            }
            return (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  isActive ? 'w-6 bg-[#007a33]' : isPast ? 'w-1.5 bg-gray-400' : 'w-1.5 bg-gray-200'
                }`}
              />
            );
          })}
        </div>
      )}

      {/* Back button on plan page */}
      {page === TOTAL_PAGES && (
        <div className="text-center animate-fadeIn">
          <button
            onClick={() => setPage(enabledPagesLength)}
            className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg bg-white/10 text-gray-300 hover:bg-white/20 hover:text-white transition-colors"
            data-testid="plan-back-btn"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            Back to previous step
          </button>
        </div>
      )}
    </div>
  );
}
